import React from 'react';
import { OptionCard, type OrderedOption } from './OptionCard';

interface OptionCardCarouselProps {
  options: OrderedOption[];
  onSelect: (opt: OrderedOption) => void;
}

export function OptionCardCarousel({ options, onSelect }: OptionCardCarouselProps) {
  const [pickedIndex, setPickedIndex] = React.useState<number | null>(null);
  const trackRef = React.useRef<HTMLDivElement | null>(null);
  const [atStart, setAtStart] = React.useState(true);
  const [atEnd, setAtEnd] = React.useState(false);

  const handleSelect = (opt: OrderedOption) => {
    if (pickedIndex !== null) return; // single-pick only
    setPickedIndex(opt.index);
    onSelect(opt);
  };

  const updateEdgeState = React.useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setAtStart(scrollLeft <= 0);
    setAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
  }, []);

  React.useEffect(() => {
    updateEdgeState();
  }, [options.length, updateEdgeState]);

  const scrollByOneCard = (direction: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const firstItem = el.querySelector<HTMLElement>('.ww-option-carousel-item');
    const delta = firstItem ? firstItem.offsetWidth + 16 /* gap */ : Math.round(el.clientWidth / 2.5);
    el.scrollBy({ left: direction * delta, behavior: 'smooth' });
    // async update after scroll
    setTimeout(updateEdgeState, 300);
  };

  return (
    <div className="ww-option-carousel">
      <div
        className="ww-option-carousel-track"
        ref={trackRef}
        onScroll={updateEdgeState}
        style={{ ['--cards-per-view' as any]: 2.5 }}
      >
        {options.map((opt) => (
          <div className="ww-option-carousel-item" key={opt.index}>
            <OptionCard
              option={opt}
              onSelect={handleSelect}
              selected={pickedIndex === opt.index}
              disabled={pickedIndex !== null && pickedIndex !== opt.index}
            />
          </div>
        ))}
      </div>
      <div className="ww-option-carousel-controls">
        <button
          type="button"
          className="ww-option-carousel-arrow"
          onClick={() => scrollByOneCard(-1)}
          disabled={atStart}
          aria-label="Scroll left"
        />
        <button
          type="button"
          className="ww-option-carousel-arrow"
          onClick={() => scrollByOneCard(1)}
          disabled={atEnd}
          aria-label="Scroll right"
        />
      </div>
    </div>
  );
}


